import { useEffect, useState } from "react"
import { INewReplenishmentResponse } from "interfaces/IReplenishment"
import { useLocation, useNavigate } from "react-router"
import Timer from "shared/Timer"
import { Header, InfoText, StrongText } from "./styles"
import { Box, MenuItem, Select, Typography, CircularProgress } from "@mui/material"
import { toast } from "react-toastify"
import { replenishmentService } from "services/replenishmentService"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import { ModalConfirmStockout } from "components/ModalConfirmStockout"
import { stockoutService } from "services/stockoutService"
import { configsService } from "services/configsService"
import { IConfigDataResponse } from "interfaces/IConfig"
import { productService } from "services/productService"
import { batchService, Batch } from "services/batchService"
import Cam from "utils/Cam"

const Replenishing = () => {
  const [successFeedback, setSuccessFeedback] = useState(false)
  const [openModalConfirmStockout, setOpenModalConfirmStockout] = useState(false)
  const [stockout, setStockout] = useState(false)
  const [isLoading, setisLoading] = useState(false)
  const [selectedMarketNumber, setSelectedMarketNumber] = useState(0)
  const [newReplenishment, setNewReplenishment] = useState<INewReplenishmentResponse | undefined>(
    undefined,
  )
  const [config, setConfig] = useState<IConfigDataResponse>({
    id: 0,
    created: "",
    totalMarkets: 0,
    modifiedById: 0,
    modifiedByUsername: "",
    useScanner: false,
  })
  const [inputValue, setInputValue] = useState("")
  const [batchId, setBatchId] = useState<number | null>(null)
  const [showScanner, setShowScanner] = useState(true)
  const [isLoadingBatches, setIsLoadingBatches] = useState(false)
  const [batches, setBatches] = useState<Batch[]>([])
  const [selectedBatch, setSelectedBatch] = useState<number | null>(null)
  const isInvalidMarketNumber =
    !selectedMarketNumber && !newReplenishment?.replenishmentData.marketNumber
  const disableConfirmBotton =
    !inputValue ||
    isInvalidMarketNumber ||
    isLoading ||
    Number(inputValue) < 1 ||
    isNaN(Number(inputValue))
  const disableStockoutBotton = !!inputValue || isLoading
  const location = useLocation()
  const navigate = useNavigate()

  const generateNumberList =
    config.totalMarkets > 0 ? Array.from({ length: config.totalMarkets }, (_, i) => i + 1) : []

  const handleInputChange = (event: any) => {
    setInputValue(event.target.value)
  }

  const handleMarketNumberChange = (event: any) => {
    setSelectedMarketNumber(event.target.value)
  }

  const handleGetBatches = async () => {
    try {
      setIsLoadingBatches(true)
      const response = await batchService.getAllBaseProducts({
        sku: newReplenishment?.skuData?.sku,
        pageSize: 100,
      })
      const batches = response.results[0]?.Batchs || []
      setBatches(batches)
    } catch (error: any) {
      toast(error?.message ?? "Ocorreu um erro ao buscar os lotes, dá um alô pro time de tech.", {
        type: "error",
      })
    } finally {
      setIsLoadingBatches(false)
    }
  }

  const handleBatchChange = (event: any) => {
    setSelectedBatch(event.target.value)
    setBatchId(event.target.value)
  }

  const handleCreateReplenishment = async () => {
    try {
      setisLoading(true)
      const data = {
        marketNumber: newReplenishment?.replenishmentData?.marketNumber || selectedMarketNumber,
        productId: newReplenishment?.skuData?.productId,
        quantity: Number(inputValue),
        workId: newReplenishment?.workData?.id,
        batchId: batchId,
      }
      await replenishmentService.createReplenishment(data)
      setSuccessFeedback(true)
    } catch (error: any) {
      toast(error?.message ?? "Ocorreu um erro, dá um alô pro time de tech.", { type: "error" })
    } finally {
      setisLoading(false)
    }
  }

  const handleGetConfigs = async () => {
    try {
      setisLoading(true)
      const response_config = await configsService.getConfig()
      setConfig(response_config)
    } catch (error: any) {
      toast(error?.message ?? "Ocorreu um erro, dá um alô pro time de tech.", { type: "error" })
    } finally {
      setisLoading(false)
    }
  }

  const handleConfirmStockout = () => {
    setOpenModalConfirmStockout(true)
  }

  const handleCreateStockout = async () => {
    try {
      setisLoading(true)
      const data = {
        productId: newReplenishment?.skuData?.productId,
        workId: newReplenishment?.workData?.id,
      }
      await stockoutService.createStockout(data)
      setStockout(true)
      setSuccessFeedback(true)
    } catch (error: any) {
      toast(error?.message ?? "Ocorreu um erro, dá um alô pro time de tech.", { type: "error" })
    } finally {
      setisLoading(false)
      setOpenModalConfirmStockout(false)
    }
  }

  const handleNewReplenish = () => {
    setisLoading(true)
    setSuccessFeedback(false)
    navigate("/painel-de-operador/abastecimento")
    setisLoading(false)
  }

  const handleScanResult = async (code: string) => {
    try {
      setisLoading(true)
      const response = await productService.checkBatch(code, newReplenishment?.skuData?.sku || "")
      if (response.skuData) {
        setNewReplenishment(prev => ({
          ...prev!,
          skuData: {
            productId: response.skuData.productId,
            sku: response.skuData.sku,
            purchaseValue: response.skuData.purchaseValue.toString(),
            portionType: response.skuData.portionType,
            separationOrder: response.skuData.separationOrder,
            name: response.skuData.name,
            productBaseId: response.skuData.productBaseId,
          },
        }))
        setBatchId(response.batch_id)
        setShowScanner(false)
      } else {
        toast("SKU não encontrado", { type: "error" })
      }
    } catch (error: any) {
      toast(error?.message ?? "Ocorreu um erro ao buscar o SKU, dá um alô pro time de tech.", {
        type: "error",
      })
    } finally {
      setisLoading(false)
    }
  }

  const handleScanAgain = () => {
    setShowScanner(true)
    setBatchId(null)
  }

  useEffect(() => {
    if (location.state && location.state.newReplenishment) {
      setNewReplenishment(location.state.newReplenishment)
      handleGetConfigs()
      if (!config.useScanner) {
        handleGetBatches()
      }
    }
  }, [location.state, config.useScanner])

  return (
    <Box>
      <Header component="header">
        <Box>
          <InfoText>
            Produto:{" "}
            <StrongText>{newReplenishment?.skuData?.name || "Ainda não definido"}</StrongText>
          </InfoText>
          <InfoText>
            Porcionamento:{" "}
            <StrongText>
              {newReplenishment?.skuData?.purchaseValue}{" "}
              {newReplenishment?.skuData?.portionType === "peso"
                ? "kg"
                : newReplenishment?.skuData?.portionType === "unidade"
                  ? "un"
                  : "Ainda não definido"}
            </StrongText>
          </InfoText>
          <InfoText>
            Etapa:{" "}
            <StrongText>
              {newReplenishment?.skuData?.separationOrder || "Ainda não definido"}
            </StrongText>
          </InfoText>
          <InfoText>
            Mercado:{" "}
            <StrongText>
              {newReplenishment?.replenishmentData?.marketNumber || "Ainda não definido"}
            </StrongText>
          </InfoText>
          <InfoText>
            Sugerido:{" "}
            <StrongText>
              {newReplenishment?.suggestedQuantity
                ? `${newReplenishment.suggestedQuantity} un`
                : "Ainda não definido"}
            </StrongText>
          </InfoText>
          {config.useScanner && (
            <InfoText>
              Lote: <StrongText>{batchId || "Ainda não definido"}</StrongText>
            </InfoText>
          )}
          <InfoText>
            Tempo de abastecimento:
            <StrongText>
              <Timer time={newReplenishment?.workData?.begin} stop={successFeedback} />
            </StrongText>
          </InfoText>
        </Box>
      </Header>
      {successFeedback ? (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          marginTop="50px"
        >
          <CheckCircleIcon sx={{ fontSize: 100, color: "#FF8C42" }} />
          <Typography
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: "500",
              fontSize: "21px",
              lineHeight: "33.6px",
              letterSpacing: "1%",
              marginTop: "20px",
            }}
          >
            {stockout ? "Produto sem estoque informado! 👍" : "Produto reabastecido! 👍"}
          </Typography>
          <button
            onClick={handleNewReplenish}
            disabled={isLoading}
            style={{
              marginTop: "40px",
              padding: "10px 20px",
              fontSize: "18px",
              backgroundColor: "#FF8C42",
              color: "white",
              border: "none",
              borderRadius: "16px",
              cursor: "pointer",
              width: "300px",
            }}
          >
            Abastecer novo produto
          </button>
        </Box>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "50px",
          }}
        >
          <Typography
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: "500",
              fontSize: "28px",
              lineHeight: "33.6px",
              letterSpacing: "1%",
              marginBottom: "2rem",
            }}
          >
            Retirada do estoque
          </Typography>
          {config.useScanner && showScanner ? (
            <div style={{ marginBottom: "20px", textAlign: "center" }}>
              <Typography
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: "500",
                  fontSize: "20px",
                  lineHeight: "33.6px",
                  letterSpacing: "1%",
                  marginBottom: "1rem",
                }}
              >
                Escaneie o código QR do SKU
              </Typography>
              <Cam onSubmit={handleScanResult} loading={isLoading} />
              <button
                disabled={disableStockoutBotton}
                onClick={handleConfirmStockout}
                style={{
                  marginTop: "15px",
                  padding: "10px 20px",
                  fontSize: "18px",
                  backgroundColor: disableStockoutBotton ? "#e57373" : "#EB001B",
                  color: "white",
                  border: "none",
                  borderRadius: "16px",
                  cursor: disableStockoutBotton ? "not-allowed" : "pointer",
                  width: "300px",
                }}
              >
                Produto sem estoque
              </button>
            </div>
          ) : (
            <>
              {config.useScanner && batchId && (
                <div style={{ marginBottom: "20px", textAlign: "center" }}>
                  <button
                    onClick={handleScanAgain}
                    style={{
                      padding: "10px 20px",
                      fontSize: "18px",
                      backgroundColor: "#FF8C42",
                      color: "white",
                      border: "none",
                      borderRadius: "16px",
                      cursor: "pointer",
                      width: "300px",
                    }}
                  >
                    Escanear novamente
                  </button>
                </div>
              )}
              {!config.useScanner && (
                <div style={{ marginBottom: "20px", textAlign: "center" }}>
                  <Typography
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: "500",
                      fontSize: "24px",
                      lineHeight: "33.6px",
                      letterSpacing: "1%",
                      marginBottom: "1rem",
                    }}
                  >
                    Selecione o lote
                  </Typography>
                  <Select
                    value={selectedBatch || ""}
                    onChange={handleBatchChange}
                    disabled={isLoadingBatches}
                    displayEmpty
                    renderValue={selected => {
                      if (!selected) {
                        return <em>Selecione...</em>
                      }
                      return selected
                    }}
                    style={{
                      fontSize: "18px",
                      width: "260px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      textAlign: "center",
                      height: "2.5rem",
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                          overflowY: "auto",
                        },
                      },
                    }}
                  >
                    {isLoadingBatches ? (
                      <CircularProgress />
                    ) : batches.length === 0 ? (
                      <MenuItem disabled>
                        Não há lotes disponíveis para este produto. Por favor, entre em contato com
                        o PCP.
                      </MenuItem>
                    ) : (
                      batches.map(batch => (
                        <MenuItem key={batch.id} value={batch.batchId}>
                          {batch.batchId}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </div>
              )}
              <Typography
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: "500",
                  fontSize: "24px",
                  lineHeight: "33.6px",
                  letterSpacing: "1%",
                }}
              >
                Quantos pacotes?
              </Typography>

              <input
                type="number"
                min="1"
                value={inputValue}
                onChange={handleInputChange}
                style={{
                  marginTop: "20px",
                  padding: "10px",
                  fontSize: "18px",
                  width: "260px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  textAlign: "center",
                }}
                placeholder="Ex. 40"
              />
              {!newReplenishment?.replenishmentData?.marketNumber && (
                <div style={{ marginBottom: "20px", textAlign: "center" }}>
                  <label
                    htmlFor="marketNumber"
                    style={{
                      display: "block",
                      fontSize: "18px",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    <Typography
                      style={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: "500",
                        fontSize: "24px",
                        lineHeight: "33.6px",
                        letterSpacing: "1%",
                        marginTop: "2rem",
                      }}
                    >
                      Qual o mercado?
                    </Typography>
                  </label>
                  <Select
                    id="marketNumber"
                    value={selectedMarketNumber}
                    onChange={handleMarketNumberChange}
                    disabled={isLoading}
                    style={{
                      fontSize: "18px",
                      width: "260px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      textAlign: "center",
                      height: "2.5rem",
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                          overflowY: "auto",
                        },
                      },
                    }}
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    {generateNumberList.map(number => (
                      <MenuItem key={number} value={number}>
                        Mercado {number}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              )}
              <button
                disabled={disableConfirmBotton}
                onClick={handleCreateReplenishment}
                style={{
                  marginTop: "40px",
                  padding: "10px 20px",
                  fontSize: "18px",
                  backgroundColor: disableConfirmBotton ? "#FFC78F" : "#FF8C42",
                  cursor: disableConfirmBotton ? "not-allowed" : "pointer",
                  color: "white",
                  border: "none",
                  borderRadius: "16px",
                  width: "300px",
                }}
              >
                Reabastecer
              </button>
            </>
          )}
        </div>
      )}
      <ModalConfirmStockout
        handleClose={() => setOpenModalConfirmStockout(false)}
        open={openModalConfirmStockout}
        handleConfirm={handleCreateStockout}
        isLoading={isLoading}
      />
    </Box>
  )
}

export default Replenishing
